import { useEffect, useRef, useState } from "react";
import { Eye, History, X } from "lucide-react";
import axios from "../api/axios";

export default function FileHistory({ file, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    axios.get(`/api/files/${file.id}/history`)
      .then(({ data }) => {
        if (active) setVersions(data.versions);
      })
      .catch(() => {
        if (active) setError("Unable to load file history. Please try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [file.id, attempt]);

  useEffect(() => {
    const previousFocus = document.activeElement;
    closeRef.current?.focus();
    return () => previousFocus?.focus();
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === "Escape") onClose();
    if (event.key === "Tab") {
      const controls = dialogRef.current.querySelectorAll("button, a[href]");
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="file-history-title"
        onKeyDown={handleKeyDown}
        className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h2 id="file-history-title" className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <History size={20} /> View History
            </h2>
            <p className="text-sm text-gray-600 mt-1 break-words">{file.filename}</p>
          </div>
          <button ref={closeRef} onClick={onClose} aria-label="Close file history"
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600">
            <X size={20} />
          </button>
        </div>
        {loading ? (
          <p role="status" className="text-gray-600 py-6">Loading file history...</p>
        ) : error ? (
          <div role="alert" className="text-red-700">
            <p>{error}</p>
            <button onClick={() => setAttempt((value) => value + 1)}
              className="mt-3 px-3 py-2 bg-orange-100 text-orange-700 rounded-lg">Try again</button>
          </div>
        ) : (
          <div className="space-y-3">
            {versions.length === 1 && (
              <p className="text-sm text-gray-500">No replacements yet. This is the original upload.</p>
            )}
            {versions.map((version) => (
              <div key={version.version_number}
                className={`border rounded-lg p-4 ${version.is_current ? "border-green-200 bg-green-50" : "border-gray-200"}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="font-medium text-gray-900">Version {version.version_number}</span>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${version.is_current ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                    {version.is_current ? "Current Version" : "Previous Version"}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-800 break-words">{version.filename}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {version.version_number === 1 ? "Uploaded" : "Replaced"} by {version.user?.name || "Unknown"}
                </p>
                <p className="text-sm text-gray-600">
                  {new Date(version.uploaded_at).toLocaleString(undefined, {
                    year: "numeric", month: "short", day: "numeric",
                    hour: "2-digit", minute: "2-digit", second: "2-digit", timeZoneName: "short",
                  })}
                </p>
                <a href={version.url} target="_blank" rel="noopener noreferrer"
                  aria-label={`View version ${version.version_number}`}
                  className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-blue-700 hover:underline">
                  <Eye size={14} /> View file
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
